import React from 'react';

/**
 * Email Preview Component
 * Shows exactly how the email will appear when sent
 */
export function EmailPreview({ 
  emailScript,
  organisationName = 'Your Organisation',
  logoUrl = null,
  brandColor = '#1a365d',
  sampleData = {}
}) {
  // Default sample data for preview
  const data = {
    contact_name: sampleData.contact_name || 'John Smith',
    site_name: sampleData.site_name || 'Sample Site',
    customer_name: sampleData.customer_name || 'Sample Customer',
    scheduled_date: sampleData.scheduled_date || '15 Mar 2026',
    date_range: sampleData.date_range || '01 Mar 2026 to 31 Mar 2026',
    jobs_count: sampleData.jobs_count || '3',
    organisation_name: organisationName,
    ...sampleData
  };

  // Sample jobs table HTML
  const jobsTableHtml = `
    <table style="width: 100%; border-collapse: collapse; margin: 20px 0;">
      <thead>
        <tr style="background-color: ${brandColor}; color: white;">
          <th style="padding: 12px; text-align: left;">Date(s)</th>
          <th style="padding: 12px; text-align: left;">Site</th>
          <th style="padding: 12px; text-align: left;">Service</th>
          <th style="padding: 12px; text-align: left;">Customer</th>
          <th style="padding: 12px; text-align: left;">Auditor(s)</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td style="padding: 10px; border-bottom: 1px solid #e0e0e0;">15 Mar 2026</td>
          <td style="padding: 10px; border-bottom: 1px solid #e0e0e0;">${data.site_name}</td>
          <td style="padding: 10px; border-bottom: 1px solid #e0e0e0;">Stock Audit</td>
          <td style="padding: 10px; border-bottom: 1px solid #e0e0e0;">${data.customer_name}</td>
          <td style="padding: 10px; border-bottom: 1px solid #e0e0e0;">Jane Doe</td>
        </tr>
        <tr>
          <td style="padding: 10px; border-bottom: 1px solid #e0e0e0;">22 Mar 2026</td>
          <td style="padding: 10px; border-bottom: 1px solid #e0e0e0;">${data.site_name}</td>
          <td style="padding: 10px; border-bottom: 1px solid #e0e0e0;">Food Audit</td>
          <td style="padding: 10px; border-bottom: 1px solid #e0e0e0;">${data.customer_name}</td>
          <td style="padding: 10px; border-bottom: 1px solid #e0e0e0;">John Smith</td>
        </tr>
      </tbody>
    </table>
  `;

  // Replace placeholders in email script
  let processedContent = emailScript || `<p>Dear {{contact_name}},</p>
<p>Please find below the scheduled audit details for {{site_name}}.</p>
{{jobs_table}}
<p>Best regards,<br/>{{organisation_name}}</p>`;

  // Replace all placeholders
  processedContent = processedContent
    .replace(/\{\{contact_name\}\}/g, data.contact_name)
    .replace(/\{\{client_name\}\}/g, data.contact_name)
    .replace(/\{\{recipient_name\}\}/g, data.contact_name)
    .replace(/\{\{site_name\}\}/g, data.site_name)
    .replace(/\{\{customer_name\}\}/g, data.customer_name)
    .replace(/\{\{organisation_name\}\}/g, data.organisation_name)
    .replace(/\{\{date_range\}\}/g, data.date_range)
    .replace(/\{\{scheduled_date\}\}/g, data.scheduled_date)
    .replace(/\{\{jobs_table\}\}/g, jobsTableHtml)
    .replace(/\{\{jobs_count\}\}/g, data.jobs_count);

  // Convert newlines to <br> if no HTML tags present
  if (!processedContent.includes('<p>') && !processedContent.includes('<br') && !processedContent.includes('<div>')) {
    processedContent = processedContent.replace(/\n/g, '<br/>');
  }

  const logoHtml = logoUrl 
    ? `<img src="${logoUrl}" alt="${organisationName}" style="max-width: 240px; max-height: 96px; margin-bottom: 8px; display: block; margin-left: auto; margin-right: auto;" />`
    : '';

  const disclaimer = `
    <div style="margin-top: 40px; padding-top: 20px; border-top: 1px solid #e0e0e0; font-size: 12px; color: #666;">
      <p style="margin: 5px 0;"><strong>${organisationName}</strong> - Schedule Management System</p>
      <p style="margin: 10px 0; font-size: 11px; color: #888;">
        CONFIDENTIALITY NOTICE: This email and any attachments are confidential and intended solely for the use of the named recipient(s). 
        If you have received this email in error, please notify the sender immediately and then permanently delete this email and any attachments.
      </p>
    </div>
  `;

  const fullEmailHtml = `
    <div style="font-family: Arial, sans-serif; line-height: 1.6; color: #333; max-width: 650px; margin: 0 auto; background-color: #ffffff; border: 1px solid #e0e0e0; border-radius: 8px; overflow: hidden;">
      <div style="background-color: ${brandColor}; color: white; padding: 12px 20px; text-align: center;">
        ${logoHtml}
        <h1 style="margin: 0; font-size: 20px; color: #000000;">${organisationName}</h1>
        <h2 style="margin: 4px 0 0 0; font-size: 14px; font-weight: normal; color: #000000;">Schedule Notification</h2>
      </div>
      <div style="padding: 30px;">
        ${processedContent}
      </div>
      <div style="padding: 0 30px 30px 30px;">
        ${disclaimer}
      </div>
    </div>
  `;

  return (
    <div className="email-preview bg-gray-100 p-4 rounded-lg">
      <div className="text-xs text-muted-foreground mb-2 flex items-center gap-2">
        <span className="bg-blue-100 text-blue-800 px-2 py-0.5 rounded">Preview</span>
        <span>This is how your email will appear to recipients</span>
      </div>
      <div 
        className="bg-white shadow-lg"
        dangerouslySetInnerHTML={{ __html: fullEmailHtml }}
      />
    </div>
  );
}

export default EmailPreview;
